var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var DateDiffConfig_1;
/* eslint-disable import/no-duplicates */
import '@symon-ai/wisepipe-base-pipe'; // force import for JsonSchemaV2
import { DateUnitType as DateUnitTypeOrig, ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { DateColumn } from './dateOperand/dateColumn';
import { DateLiteral } from './dateOperand/dateLiteral';
import './dateOperand/dateOperand'; // force import for JsonSchemaV2
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { dateDiffConfigSchemaValidation } from '../validations.generated';
// leave it reexported to save updating all references
export var DateUnitType = DateUnitTypeOrig;
export const dateDiffConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:DateDiffConfig',
    type: 'object',
    properties: {
        lhs: { $ref: 'urn:wisepipe-common-pipe:DateOperand' },
        rhs: { $ref: 'urn:wisepipe-common-pipe:DateOperand' },
        unit: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:EnumToolVariable'
                },
                { type: 'string', enum: Object.values(DateUnitType) }
            ]
        },
        alias: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:StringToolVariable'
                },
                { type: 'string', minLength: 1, maxLength: 150 }
            ]
        }
    },
    required: ['lhs', 'rhs', 'unit', 'alias']
};
let DateDiffConfig = DateDiffConfig_1 = class DateDiffConfig extends ToolConfig {
    constructor(lhs, rhs, unit, alias, customFormats) {
        super(ToolType.DATE_DIFF, customFormats);
        this.lhs = lhs;
        this.rhs = rhs;
        this.unit = unit;
        this.alias = alias;
        JSON.validate(this, DateDiffConfig_1);
    }
    // eslint-disable-next-line class-methods-use-this
    get in() {
        return DateDiffConfig_1.in;
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return DateDiffConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return DateDiffConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return DateDiffConfig_1.outs;
    }
    static get in() {
        return DateDiffConfig_1.ins[0];
    }
    static get out() {
        return DateDiffConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.DATE_DIFF.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['datediff', 'month', 'year', 'day'], true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new DateDiffConfig_1(this.lhs.applyParameters(parameters), this.rhs.applyParameters(parameters), baseVals.unit, baseVals.alias, this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            lhs: this.lhs.getBaseValues(parameters),
            rhs: this.rhs.getBaseValues(parameters),
            unit: parameters.getEnumVal(this.unit),
            alias: parameters.getStrVal(this.alias)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        const exp = tsb.export(this.out);
        if (source) {
            const parameters = iConfigHelper.getParameters();
            const dependents = this.getDependents(exp, this.in, [
                ...Array.from(this.lhs.getColArgs(parameters)),
                ...Array.from(this.rhs.getColArgs(parameters))
            ]);
            exp.copyAll(this.in).makeNew(new Column(parameters.getStrVal(this.alias), 'number'), dependents, true);
        }
        exp.initializeColumnFormats().setColumnFormats(this.customFormats);
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, ins) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, ins);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        const parameters = iConfigHelper.getParameters();
        const baseVals = this.getBaseValues(parameters);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        if ((this.lhs instanceof DateLiteral && !baseVals.lhs.value) ||
            (this.rhs instanceof DateLiteral && !baseVals.rhs.value)) {
            return ToolValidationResult.getBadMetadataResult('Date literal value is missing');
        }
        if ((this.lhs instanceof DateColumn && !baseVals.lhs.name) ||
            (this.rhs instanceof DateColumn && !baseVals.rhs.name)) {
            return ToolValidationResult.getBadMetadataResult('Date column name is missing');
        }
        const inputs = new Map();
        this.lhs.addInputReq(parameters, inputs);
        this.rhs.addInputReq(parameters, inputs);
        const sourceValidationResult = validateSourceWithDetail(source, inputs, [baseVals.alias]);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
DateDiffConfig = DateDiffConfig_1 = __decorate([
    JsonSchemaV2(dateDiffConfigSchema, dateDiffConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.DATE_DIFF)
], DateDiffConfig);
export { DateDiffConfig };
//# sourceMappingURL=dateDiffConfig.js.map